import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useControls, folder } from 'leva'

import { TABELLONI, listaMedia, isImmagine } from '../../config/pubblicita'

// ============================================================
// Tabelloni pubblicitari, alimentati da file caricati su Shopify
// (Contenuto → File) — vedi src/config/pubblicita.js.
//
// DUE MODALITÀ, scelte in automatico:
//  • LAMELLE (trivision): come i cartelloni stradali veri — la
//    superficie è fatta di lamelle verticali a sezione triangolare
//    che ruotano "a onda" e scoprono il manifesto successivo.
//    Serve che i contenuti siano 2 o 3 IMMAGINI.
//  • PIATTO: schermo unico con playlist (necessario per i video, o
//    con più di 3 contenuti). I video sono muti: i browser bloccano
//    l'autoplay con audio.
//
// In entrambe: cornice metallica, fondale scuro, e tutto va in
// PAUSA quando sei lontano o la scheda non è in primo piano.
// ============================================================

const DURATA_IMMAGINE = 6 // secondi, se non specificato nel config
const SFASAMENTO = 0.13   // ritardo (s) tra una lamella e la successiva (tarato dall'utente)
const VELOCITA_ROT = 3    // rapidità della rotazione delle lamelle

// Slider di calibrazione generati dal config (come per gli scaffali)
const SCHEMA = (() => {
  const schema = {
    // ritmo dell'onda delle lamelle, regolabile in diretta
    velocitaRot: { value: VELOCITA_ROT, min: 0.5, max: 12, step: 0.1, label: 'velocità rotazione' },
    sfasamento: { value: SFASAMENTO, min: 0, max: 0.4, step: 0.005, label: 'sfasamento onda' },
  }
  TABELLONI.forEach((t) => {
    schema[t.nome] = folder(
      {
        [`${t.nome}_x`]: { value: t.position[0], min: -12, max: 12, step: 0.05, label: 'X' },
        [`${t.nome}_y`]: { value: t.position[1], min: 0, max: 7, step: 0.05, label: 'Y' },
        [`${t.nome}_z`]: { value: t.position[2], min: -12, max: 12, step: 0.05, label: 'Z' },
        [`${t.nome}_rot`]: { value: t.rotationY, min: -Math.PI, max: Math.PI, step: 0.01, label: 'rotazione' },
        [`${t.nome}_w`]: { value: t.larghezza, min: 0.5, max: 10, step: 0.05, label: 'larghezza' },
        [`${t.nome}_h`]: { value: t.altezza, min: 0.3, max: 6, step: 0.05, label: 'altezza' },
      },
      { collapsed: true }
    )
  })
  return schema
})()

export default function Billboard() {
  const attivi = useMemo(() => TABELLONI.filter((t) => listaMedia(t).length > 0), [])
  const controls = useControls('Pubblicità', SCHEMA, { collapsed: true })

  return attivi.map((t) => (
    <Tabellone
      key={t.nome}
      config={t}
      position={[controls[`${t.nome}_x`], controls[`${t.nome}_y`], controls[`${t.nome}_z`]]}
      rotationY={controls[`${t.nome}_rot`]}
      larghezza={controls[`${t.nome}_w`]}
      altezza={controls[`${t.nome}_h`]}
      velocitaRot={controls.velocitaRot}
      sfasamento={controls.sfasamento}
    />
  ))
}

// ============================================================
// Tabellone: carica i media, sceglie la modalità, gestisce la
// pausa per distanza e disegna la cornice
// ============================================================
function Tabellone({ config, position, rotationY, larghezza, altezza, velocitaRot, sfasamento }) {
  const group = useRef()
  const frame = useRef(0)
  const acceso = useRef(false) // true quando sei abbastanza vicino
  const [caricate, setCaricate] = useState(0)
  // Il contatore vive in un ref: se l'immagine è già in cache la
  // callback di load parte DURANTE il render, e aggiornare lo stato
  // lì è un errore React ("state update on a component that hasn't
  // mounted"). Il ref accumula sempre, lo stato si allinea a mount.
  const contaCaricate = useRef(0)
  const montato = useRef(false)

  // Playlist: le immagini si caricano subito (leggere, e così non
  // c'è sfarfallio al cambio); dalla callback si ricava la
  // proporzione reale del file
  const clip = useMemo(() => {
    const loader = new THREE.TextureLoader()
    loader.setCrossOrigin('anonymous')
    return listaMedia(config).map((url) => {
      if (!isImmagine(url)) return { url, immagine: false, texture: null, aspetto: null }
      const voce = { url, immagine: true, texture: null, aspetto: null }
      voce.texture = loader.load(url, (tex) => {
        voce.aspetto = tex.image.width / tex.image.height
        contaCaricate.current += 1
        if (montato.current) setCaricate(contaCaricate.current)
      })
      voce.texture.colorSpace = THREE.SRGBColorSpace
      return voce
    })
  }, [config])

  useEffect(() => {
    montato.current = true
    // allinea lo stato a ciò che si è caricato durante il render
    setCaricate(contaCaricate.current)
    return () => {
      montato.current = false
      clip.forEach((c) => c.texture?.dispose())
    }
  }, [clip])

  // Le lamelle hanno senso con 2-3 immagini (come i cartelloni veri):
  // con dei video, o con più contenuti, si usa lo schermo piatto
  const modalita = useMemo(() => {
    if (config.stile === 'piatto' || config.stile === 'lamelle') return config.stile
    const soloImmagini = clip.every((c) => c.immagine)
    return soloImmagini && clip.length >= 2 && clip.length <= 3 ? 'lamelle' : 'piatto'
  }, [clip, config.stile])

  // Accendi/spegni in base alla distanza (controllo 4 volte al secondo)
  // NB: getWorldPosition e non camera.position — la camera è figlia
  // del rig delle scosse, quindi la sua position è solo l'offset
  const posCamera = useRef(new THREE.Vector3()).current
  useFrame(({ camera }) => {
    if (++frame.current % 15 !== 0 || !group.current) return
    camera.getWorldPosition(posCamera)
    const vicino = posCamera.distanceTo(group.current.position) <= config.distanzaMax
    acceso.current = vicino && !document.hidden
  })

  const tutteCaricate = caricate >= clip.filter((c) => c.immagine).length

  return (
    <group ref={group} position={position} rotation={[0, rotationY, 0]}>
      <Cornice larghezza={larghezza} altezza={altezza} />
      {modalita === 'lamelle' ? (
        <Lamelle
          clip={clip}
          config={config}
          larghezza={larghezza}
          altezza={altezza}
          acceso={acceso}
          pronte={tutteCaricate}
          velocitaRot={velocitaRot}
          sfasamento={sfasamento}
        />
      ) : (
        <Piatto
          clip={clip}
          config={config}
          larghezza={larghezza}
          altezza={altezza}
          acceso={acceso}
        />
      )}
    </group>
  )
}

// ============================================================
// Cornice metallica + fondale scuro (il fondale fa anche da
// "bande nere" quando il manifesto non riempie tutto il pannello)
// ============================================================
function Cornice({ larghezza, altezza }) {
  const sp = Math.min(0.12, altezza * 0.06) // spessore del profilato
  const prof = 0.22                          // profondità della struttura

  return (
    <group>
      {/* fondale scuro, dietro le lamelle */}
      <mesh position={[0, 0, -prof / 2 + 0.01]}>
        <planeGeometry args={[larghezza, altezza]} />
        <meshBasicMaterial color="#0b0d12" />
      </mesh>

      {/* profilati: sopra, sotto, sinistra, destra */}
      {[
        { args: [larghezza + sp * 2, sp, prof], pos: [0, altezza / 2 + sp / 2, 0] },
        { args: [larghezza + sp * 2, sp, prof], pos: [0, -altezza / 2 - sp / 2, 0] },
        { args: [sp, altezza, prof], pos: [-larghezza / 2 - sp / 2, 0, 0] },
        { args: [sp, altezza, prof], pos: [larghezza / 2 + sp / 2, 0, 0] },
      ].map((b, i) => (
        <mesh key={i} position={b.pos}>
          <boxGeometry args={b.args} />
          {/* metallo chiaro: prende i riflessi dalla cubemap della scena */}
          <meshStandardMaterial color="#c8ccd2" metalness={0.55} roughness={0.35} />
        </mesh>
      ))}
    </group>
  )
}

// ============================================================
// LAMELLE (trivision): N prismi verticali che ruotano a onda.
// Ogni faccia del prisma porta la fetta verticale di un manifesto
// diverso: ruotando tutti insieme, il cartellone "cambia poster".
// ============================================================
function Lamelle({ clip, config, larghezza, altezza, acceso, pronte, velocitaRot, sfasamento }) {
  const nLamelle = config.lamelle ?? 12
  const facce = Math.min(clip.length, 3)
  const passoAngolo = (Math.PI * 2) / facce

  const gruppi = useRef([])
  const passoTarget = useRef(0)   // quanti scatti sono stati richiesti
  const passoLamella = useRef([]) // quanti ne ha già presi ogni lamella
  const tScatto = useRef(0)       // istante dell'ultimo scatto
  const tProssimo = useRef(0)     // quando scatterà il prossimo

  // Geometria delle facce: per ogni lamella e per ogni manifesto
  // calcolo la fetta di immagine da mostrare, rispettandone le
  // proporzioni (il manifesto è centrato nel pannello, il resto
  // resta fondale scuro)
  const pezzi = useMemo(() => {
    if (!pronte) return []
    const w = larghezza / nLamelle
    // apotema del prisma regolare a `facce` lati di lato w
    const raggio = facce === 2 ? 0.012 : w / (2 * Math.tan(Math.PI / facce))
    const out = []

    for (let f = 0; f < facce; f++) {
      const aspetto = clip[f].aspetto || larghezza / altezza
      // manifesto adattato dentro il pannello, proporzioni intatte
      const wf = Math.min(larghezza, altezza * aspetto)
      const hf = wf / aspetto

      for (let s = 0; s < nLamelle; s++) {
        const xs = -larghezza / 2 + w * (s + 0.5) // centro della lamella
        const sx = Math.max(xs - w / 2, -wf / 2)  // sovrapposizione col manifesto
        const dx = Math.min(xs + w / 2, wf / 2)
        if (dx <= sx) continue // questa lamella è fuori dal manifesto

        const texture = clip[f].texture.clone()
        texture.colorSpace = THREE.SRGBColorSpace
        texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping
        texture.offset.set((sx + wf / 2) / wf, 0)
        texture.repeat.set((dx - sx) / wf, 1)
        texture.needsUpdate = true

        out.push({
          faccia: f,
          lamella: s,
          texture,
          larghezzaPiano: (dx - sx) * 0.98, // 2% di stacco = lamelle distinte
          altezzaPiano: hf,
          xLocale: (sx + dx) / 2 - xs,      // dentro il gruppo della lamella
          angolo: f * passoAngolo,
          raggio,
        })
      }
    }
    return out
    // eslint-disable-next-line
  }, [pronte, clip, nLamelle, facce, larghezza, altezza])

  useEffect(() => {
    return () => pezzi.forEach((p) => p.texture.dispose())
  }, [pezzi])

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime
    const durata = config.durataImmagine ?? DURATA_IMMAGINE

    // fermo (lontano o scheda nascosta): rimanda il prossimo scatto
    if (!acceso.current) {
      tProssimo.current = t + durata
    } else if (t >= tProssimo.current) {
      passoTarget.current += 1
      tScatto.current = t
      tProssimo.current = t + durata
    }

    const k = 1 - Math.exp(-velocitaRot * delta)
    for (let s = 0; s < nLamelle; s++) {
      const g = gruppi.current[s]
      if (!g) continue
      // la lamella parte con un piccolo ritardo rispetto alla precedente:
      // è l'onda che si vede sui cartelloni veri
      if (
        (passoLamella.current[s] ?? 0) < passoTarget.current &&
        t >= tScatto.current + s * sfasamento
      ) {
        passoLamella.current[s] = passoTarget.current
      }
      const target = -(passoLamella.current[s] ?? 0) * passoAngolo
      g.rotation.y += (target - g.rotation.y) * k
    }
  })

  if (!pronte) return null

  const w = larghezza / nLamelle
  return (
    <group>
      {Array.from({ length: nLamelle }, (_, s) => (
        <group
          key={s}
          ref={(el) => (gruppi.current[s] = el)}
          position={[-larghezza / 2 + w * (s + 0.5), 0, 0]}
        >
          {pezzi
            .filter((p) => p.lamella === s)
            .map((p) => (
              <group key={`${p.faccia}-${s}`} rotation={[0, p.angolo, 0]}>
                <mesh position={[p.xLocale, 0, p.raggio]}>
                  <planeGeometry args={[p.larghezzaPiano, p.altezzaPiano]} />
                  <meshBasicMaterial map={p.texture} toneMapped={false} side={THREE.DoubleSide} />
                </mesh>
              </group>
            ))}
        </group>
      ))}
    </group>
  )
}

// ============================================================
// PIATTO: schermo unico con playlist (video e/o immagini)
// ============================================================
function Piatto({ clip, config, larghezza, altezza, acceso }) {
  const indice = useRef(0)    // elemento corrente della playlist
  const motore = useRef(null) // { riprendi, ferma }
  const attivo = useRef(false)
  const [aspetto, setAspetto] = useState(larghezza / altezza)

  // <video> + VideoTexture creati SOLO se la playlist ne ha bisogno
  const { video, videoTexture } = useMemo(() => {
    if (clip.every((c) => c.immagine)) return { video: null, videoTexture: null }
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous' // necessario per usarlo come texture
    video.loop = clip.length === 1 // con la playlist si avanza a fine video
    video.muted = true // senza muted i browser bloccano l'autoplay
    video.playsInline = true
    video.preload = 'auto'
    const videoTexture = new THREE.VideoTexture(video)
    videoTexture.colorSpace = THREE.SRGBColorSpace
    return { video, videoTexture }
  }, [clip])

  const [mappa, setMappa] = useState(() => (clip[0].immagine ? clip[0].texture : videoTexture))

  // --- Motore della playlist ---
  useEffect(() => {
    let timer = null
    const durata = (config.durataImmagine ?? DURATA_IMMAGINE) * 1000

    const mostra = (i) => {
      clearTimeout(timer)
      indice.current = i
      const c = clip[i]
      if (c.aspetto) setAspetto(c.aspetto)
      if (c.immagine) {
        video?.pause()
        setMappa(c.texture)
        if (clip.length > 1) timer = setTimeout(prossimo, durata)
      } else {
        setMappa(videoTexture)
        if (video.src !== c.url) video.src = c.url
        video.play().catch(() => {}) // autoplay negato: ritenta al prossimo giro
      }
    }
    const prossimo = () => mostra((indice.current + 1) % clip.length)
    const onEnded = () => { if (clip.length > 1) prossimo() }
    // proporzione reale del video, nota solo a metadati caricati
    const onMeta = () => {
      if (!video.videoHeight) return
      const a = video.videoWidth / video.videoHeight
      clip[indice.current].aspetto = a
      setAspetto(a)
    }

    video?.addEventListener('ended', onEnded)
    video?.addEventListener('loadedmetadata', onMeta)
    motore.current = {
      riprendi: () => mostra(indice.current),
      ferma: () => {
        clearTimeout(timer)
        timer = null
        video?.pause()
      },
    }

    return () => {
      clearTimeout(timer)
      video?.removeEventListener('ended', onEnded)
      video?.removeEventListener('loadedmetadata', onMeta)
      motore.current = null
    }
  }, [clip, video, videoTexture, config.durataImmagine])

  // Pulizia risorse video
  useEffect(() => {
    return () => {
      if (video) {
        video.pause()
        video.removeAttribute('src')
        video.load()
      }
      videoTexture?.dispose()
    }
  }, [video, videoTexture])

  // Segue l'acceso/spento deciso dal Tabellone (distanza + visibilità)
  useFrame(() => {
    if (acceso.current === attivo.current) return
    attivo.current = acceso.current
    if (attivo.current) motore.current?.riprendi()
    else motore.current?.ferma()
  })

  // Contenuto adattato dentro la cornice, proporzioni intatte
  const h = Math.min(altezza, larghezza / aspetto)
  const w = h * aspetto

  return (
    <mesh position={[0, 0, 0.01]}>
      <planeGeometry args={[w, h]} />
      {/* toneMapped false: uno schermo deve "emettere", non subire
          la tone mapping della scena */}
      <meshBasicMaterial map={mappa} toneMapped={false} />
    </mesh>
  )
}
